import React from 'react';
import { Box, Typography, Card, CardContent, Button, List, ListItem, ListItemText, Divider, RadioGroup, FormControlLabel, Radio, IconButton, Chip, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper, CircularProgress, TextField, Autocomplete } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/api';

const Mokymai: React.FC = () => {
  const { token, user, hasPermission } = useAuth();
  const [docs, setDocs] = React.useState<any[]>([]);
  const [assignments, setAssignments] = React.useState<any[]>([]);
  const [selected, setSelected] = React.useState<any|null>(null);
  const [answers, setAnswers] = React.useState<Record<string, string>>({});
  const [submitting, setSubmitting] = React.useState(false);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [signoffDialogOpen, setSignoffDialogOpen] = React.useState(false);
  const [signoffData, setSignoffData] = React.useState<any>(null);
  const [loadingSignoffs, setLoadingSignoffs] = React.useState(false);
  const [currentDocId, setCurrentDocId] = React.useState<string | null>(null);
  
  // Editor state
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [editingDocId, setEditingDocId] = React.useState<string | null>(null);
  const [editorTitle, setEditorTitle] = React.useState('');
  const [editorDescription, setEditorDescription] = React.useState('');
  const [editorSummary, setEditorSummary] = React.useState('');
  const [editorSlug, setEditorSlug] = React.useState('');
  const [editorTags, setEditorTags] = React.useState('');
  const [editorBodyHtml, setEditorBodyHtml] = React.useState('<p>Įrašykite mokymo medžiagą čia...</p>');
  const [editorSubmitting, setEditorSubmitting] = React.useState(false);
  
  // Quiz editor state
  const [editorPassingScore, setEditorPassingScore] = React.useState(80);
  const [editorQuestions, setEditorQuestions] = React.useState<Array<{ id: string; text: string; choices: Array<{ id: string; text: string; is_correct: boolean }> }>>([]);
  
  // Assignment state
  const [assignMode, setAssignMode] = React.useState<'all'|'department'|'users'>('all');
  const [assignDept, setAssignDept] = React.useState('');
  const [assignUsers, setAssignUsers] = React.useState<Array<{ id: string; label: string }>>([]);
  const [allUsersOptions, setAllUsersOptions] = React.useState<Array<{ id: string; label: string }>>([]);
  const [dueAt, setDueAt] = React.useState('');
  const [assigning, setAssigning] = React.useState(false);

  const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  
  // Load users for assignment
  React.useEffect(() => {
    if (hasPermission('training.edit')) {
      (async () => {
        try {
          const res = await fetch(getApiUrl('/users'), { headers: { Authorization: `Bearer ${token}` } });
          if (res.ok) {
            const data = await res.json();
            const opts = (data?.users || []).map((u: any) => ({ 
              id: u.id, 
              label: `${u.firstName || ''} ${u.lastName || ''} <${u.email}>`.trim() 
            }));
            setAllUsersOptions(opts);
          }
        } catch {}
      })();
    }
  }, [token, hasPermission]);

  const load = React.useCallback(async () => {
    try {
      // For admins, load all documents; for regular users, load assigned ones
      const endpoint = hasPermission('training.edit') 
        ? '/training/admin/documents'
        : '/training/my/trainings';
      const res = await fetch(getApiUrl(endpoint), { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        setDocs([]);
        setAssignments([]);
        return;
      }
      let data: any = {};
      try { data = await res.json(); } catch {}
      // Admin endpoint returns {items: []}, regular endpoint returns {documents: []}
      setDocs(data.items || data.documents || []);
      setAssignments(data.assignments || []);
    } catch (e) {
      setDocs([]);
      setAssignments([]);
    }
  }, [token, hasPermission]);

  React.useEffect(() => {
    load();
  }, [load]);

  const openDoc = async (id: string) => {
    try {
      setSuccess(null);
      setError(null);
      const res = await fetch(getApiUrl(`/training/documents/${id}`), { headers: { Authorization: `Bearer ${token}` } });
      let data: any = {};
      try { data = await res.json(); } catch {}
      if (!res.ok) {
        // Don't show error for 404 (document not found) - just clear selection
        if (res.status === 404) {
          setSelected(null);
          await load(); // Refresh list in case document was deleted
          return;
        }
        // Only show error for other failures
        setError(data?.error || data?.details || 'Nepavyko užkrauti dokumento');
        return;
      }
      setSelected(data);
      setAnswers({});
    } catch (e) {
      // Network errors - don't show alert, just clear selection
      setSelected(null);
    }
  };

  const submit = async () => {
    if (!selected?.document?.id) return;
    setSubmitting(true);
    setSuccess(null);
    setError(null);
    try {
      const res = await fetch(getApiUrl(`/training/documents/${selected.document.id}/submit`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ answers })
      });
      const data = await res.json();
      if (!res.ok) {
        setError(`Nepavyko: ${data.error || 'Quiz failed'}${data.score !== undefined ? ` (score: ${data.score})` : ''}`);
      } else {
        setSuccess('Sėkmingai pasirašyta');
        // Update selected state to show signed status
        setSelected((prev: any) => ({ ...prev, signed: data.signed || true, score: data.score }));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const openEditor = async (docId: string | null = null) => {
    if (docId) {
      // Load existing document for editing
      try {
        const res = await fetch(getApiUrl(`/training/admin/documents/${docId}`), { 
          headers: { Authorization: `Bearer ${token}` } 
        });
        if (res.ok) {
          const data = await res.json();
          const doc = data.document;
          setEditorTitle(doc.title || '');
          setEditorDescription(doc.description || '');
          setEditorSummary(doc.summary || '');
          setEditorSlug(doc.slug || '');
          setEditorTags(doc.tags || '');
          setEditorBodyHtml(doc.body_html || '<p>Įrašykite mokymo medžiagą čia...</p>');
          setEditingDocId(docId);
          
          // Load quiz if exists
          if (data.quiz) {
            setEditorPassingScore(data.quiz.passing_score || 80);
            const qs = (data.quiz.questions || []).map((q: any) => ({
              id: generateId(),
              text: q.text,
              choices: (q.choices || []).map((c: any) => ({ 
                id: generateId(), 
                text: c.text, 
                is_correct: c.is_correct 
              }))
            }));
            setEditorQuestions(qs.length ? qs : [{ 
              id: generateId(), 
              text: 'Klausimas', 
              choices: [ 
                { id: generateId(), text: 'Atsakymas A', is_correct: false }, 
                { id: generateId(), text: 'Atsakymas B', is_correct: false } 
              ] 
            }]);
          } else {
            setEditorPassingScore(80);
            setEditorQuestions([{ 
              id: generateId(), 
              text: 'Klausimas', 
              choices: [ 
                { id: generateId(), text: 'Atsakymas A', is_correct: false }, 
                { id: generateId(), text: 'Atsakymas B', is_correct: false } 
              ] 
            }]);
          }
        } else {
          setError('Nepavyko užkrauti dokumento redagavimui');
          return;
        }
      } catch (e) {
        setError('Nepavyko užkrauti dokumento');
        return;
      }
    } else {
      // New document
      setEditorTitle('');
      setEditorDescription('');
      setEditorSummary('');
      setEditorSlug('');
      setEditorTags('');
      setEditorBodyHtml('<p>Įrašykite mokymo medžiagą čia...</p>');
      setEditingDocId(null);
      setEditorPassingScore(80);
      setEditorQuestions([{ 
        id: generateId(), 
        text: 'Klausimas', 
        choices: [ 
          { id: generateId(), text: 'Atsakymas A', is_correct: false }, 
          { id: generateId(), text: 'Atsakymas B', is_correct: false } 
        ] 
      }]);
    }
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditingDocId(null);
    setEditorTitle('');
    setEditorDescription('');
    setEditorSummary('');
    setEditorSlug('');
    setEditorTags('');
    setEditorBodyHtml('<p>Įrašykite mokymo medžiagą čia...</p>');
    setEditorPassingScore(80);
    setEditorQuestions([{ 
      id: generateId(), 
      text: 'Klausimas', 
      choices: [ 
        { id: generateId(), text: 'Atsakymas A', is_correct: false }, 
        { id: generateId(), text: 'Atsakymas B', is_correct: false } 
      ] 
    }]);
  };

  const saveDocument = async () => {
    if (!editorTitle.trim()) {
      setError('Pavadinimas yra privalomas');
      return;
    }
    setEditorSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      // First save/update document
      const url = editingDocId 
        ? getApiUrl(`/training/admin/documents/${editingDocId}`)
        : getApiUrl('/training/admin/documents');
      const method = editingDocId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 
          'Content-Type': 'application/json', 
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ 
          title: editorTitle, 
          description: editorDescription, 
          summary: editorSummary, 
          body_html: editorBodyHtml, 
          slug: editorSlug, 
          tags: editorTags, 
          active: true 
        })
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data?.details || data?.error || 'Nepavyko išsaugoti');
        setEditorSubmitting(false);
        return;
      }
      const savedDocId = data.id || editingDocId;
      
      // Then save quiz if there are questions
      if (editorQuestions.length > 0 && editorQuestions.some(q => q.text.trim())) {
        const quizData = {
          passing_score: editorPassingScore,
          questions: editorQuestions
            .filter(q => q.text.trim())
            .map(q => ({
              text: q.text,
              type: 'single_choice',
              choices: q.choices
                .filter(c => c.text.trim())
                .map(c => ({
                  text: c.text,
                  is_correct: c.is_correct
                }))
            }))
        };
        
        const quizRes = await fetch(getApiUrl(`/training/admin/documents/${savedDocId}/quiz`), {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json', 
            Authorization: `Bearer ${token}` 
          },
          body: JSON.stringify(quizData)
        });
        
        if (!quizRes.ok) {
          const quizError = await quizRes.json();
          setError(`Dokumentas išsaugotas, bet nepavyko išsaugoti testo: ${quizError?.error || quizError?.details || 'Nepavyko'}`);
          setEditorSubmitting(false);
          await load();
          return;
        }
      }
      
      setSuccess(editingDocId ? 'Dokumentas atnaujintas' : 'Dokumentas sukurtas');
      await load();
      setEditorSubmitting(false);
      // Close editor after successful save/update
      closeEditor();
    } catch (e) {
      setError('Nepavyko prisijungti prie serverio');
      setEditorSubmitting(false);
    }
  };
  
  // Quiz editor helper functions
  const addQuestion = () => {
    setEditorQuestions(prev => [...prev, { 
      id: generateId(), 
      text: '', 
      choices: [ 
        { id: generateId(), text: '', is_correct: false }, 
        { id: generateId(), text: '', is_correct: false } 
      ] 
    }]);
  };
  
  const removeQuestion = (qid: string) => {
    setEditorQuestions(prev => prev.filter(q => q.id !== qid));
  };
  
  const updateQuestionText = (qid: string, text: string) => {
    setEditorQuestions(prev => prev.map(q => q.id === qid ? { ...q, text } : q));
  };
  
  const addAnswer = (qid: string) => {
    setEditorQuestions(prev => prev.map(q => 
      q.id === qid 
        ? { ...q, choices: [...q.choices, { id: generateId(), text: '', is_correct: false }] }
        : q
    ));
  };
  
  const removeAnswer = (qid: string, aid: string) => {
    setEditorQuestions(prev => prev.map(q => 
      q.id === qid 
        ? { ...q, choices: q.choices.filter(c => c.id !== aid) }
        : q
    ));
  };
  
  const updateAnswerText = (qid: string, aid: string, text: string) => {
    setEditorQuestions(prev => prev.map(q => 
      q.id === qid 
        ? { ...q, choices: q.choices.map(c => c.id === aid ? { ...c, text } : c) }
        : q
    ));
  };
  
  const markCorrect = (qid: string, aid: string) => {
    setEditorQuestions(prev => prev.map(q => 
      q.id === qid 
        ? { ...q, choices: q.choices.map(c => ({ ...c, is_correct: c.id === aid })) }
        : q
    ));
  };
  
  const assignDocument = async () => {
    const docIdToAssign = editingDocId;
    if (!docIdToAssign) {
      setError('Pirma išsaugokite dokumentą');
      return;
    }
    setAssigning(true);
    setError(null);
    try {
      let totalAssigned = 0;
      if (assignMode === 'all') {
        const res = await fetch(getApiUrl(`/training/admin/documents/${docIdToAssign}/assign`), {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json', 
            Authorization: `Bearer ${token}` 
          },
          body: JSON.stringify({ department: null, due_at: dueAt || null })
        });
        const data = await res.json();
        if (!res.ok) {
          setError(data?.details || data?.error || 'Nepavyko priskirti');
          setAssigning(false);
          return;
        }
        totalAssigned = data.count || 0;
      } else if (assignMode === 'department') {
        if (!assignDept) {
          setError('Pasirinkite skyrių');
          setAssigning(false);
          return;
        }
        const res = await fetch(getApiUrl(`/training/admin/documents/${docIdToAssign}/assign`), {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json', 
            Authorization: `Bearer ${token}` 
          },
          body: JSON.stringify({ department: assignDept, due_at: dueAt || null })
        });
        const data = await res.json();
        if (!res.ok) {
          setError(data?.details || data?.error || 'Nepavyko priskirti');
          setAssigning(false);
          return;
        }
        totalAssigned = data.count || 0;
      } else {
        setError('Priskyrimas konkretiems vartotojams bus pridėtas netrukus. Naudokite "Visiems" arba "Pagal skyrių".');
        setAssigning(false);
        return;
      }
      setSuccess(`Dokumentas priskirtas ${totalAssigned} vartotojams`);
      setAssigning(false);
      // Reset assignment form
      setAssignMode('all');
      setAssignDept('');
      setAssignUsers([]);
      setDueAt('');
    } catch (e) {
      setError('Nepavyko prisijungti prie serverio');
      setAssigning(false);
    }
  };

  const archiveDocument = async (id: string, active: boolean) => {
    if (!window.confirm(active ? 'Ar tikrai norite atkurti dokumentą?' : 'Ar tikrai norite archyvuoti dokumentą?')) return;
    try {
      setSuccess(null);
      setError(null);
      const res = await fetch(getApiUrl(`/training/admin/documents/${id}/archive`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ active })
      });
      let data: any = {}; try { data = await res.json(); } catch {}
      if (!res.ok) { setError(data?.details || data?.error || 'Nepavyko'); return; }
      await load();
      setSuccess(active ? 'Dokumentas atkurtas' : 'Dokumentas archyvuotas');
    } catch (e) {
      setError('Nepavyko prisijungti prie serverio');
    }
  };

  const deleteDocument = async (id: string) => {
    if (!window.confirm('Ar tikrai norite ištrinti šį dokumentą? Šis veiksmas negrįžtamas!')) return;
    try {
      setSuccess(null);
      setError(null);
      const res = await fetch(getApiUrl(`/training/admin/documents/${id}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      let data: any = {}; try { data = await res.json(); } catch {}
      if (!res.ok) { setError(data?.details || data?.error || 'Nepavyko ištrinti'); return; }
      await load();
      setSuccess('Dokumentas ištrintas');
    } catch (e) {
      setError('Nepavyko prisijungti prie serverio');
    }
  };

  const loadSignoffStatus = async (docId: string) => {
    setCurrentDocId(docId);
    setLoadingSignoffs(true);
    setSignoffData(null);
    try {
      const res = await fetch(getApiUrl(`/training/admin/documents/${docId}/signoffs`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      let data: any = {};
      try { data = await res.json(); } catch {}
      if (!res.ok) {
        setError(data?.details || data?.error || 'Nepavyko užkrauti pasirašymų');
        setSignoffDialogOpen(false);
        return;
      }
      setSignoffData(data);
      setSignoffDialogOpen(true);
    } catch (e) {
      setError('Nepavyko prisijungti prie serverio');
      setSignoffDialogOpen(false);
    } finally {
      setLoadingSignoffs(false);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Mokymai</Typography>
        {hasPermission('training.edit') && (
          <Button variant="contained" onClick={() => openEditor(null)}>Naujas dokumentas</Button>
        )}
      </Box>
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Box display="flex" gap={2}>
        <Box flex={1}>
          <Card>
            <CardContent>
              <Typography variant="h6">Priskirti mokymai</Typography>
              <List>
                {docs.length === 0 ? (
                  <ListItem>
                    <ListItemText primary="Nėra priskirtų mokymų" secondary="Jūsų mokymai bus rodomi čia, kai jie bus jums priskirti" />
                  </ListItem>
                ) : (
                  docs.map((d) => (
                    <React.Fragment key={d.id}>
                      <ListItem>
                        <ListItemText 
                          primary={d.title}
                          secondaryTypographyProps={{ component: 'div' }}
                          secondary={
                            <Box display="flex" flexDirection="column" gap={1}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="body2" color="text.secondary" component="span">Versija {d.version}</Typography>
                                <Chip 
                                  label={d.signed ? 'Pasirašyta' : 'Nepasirašyta'} 
                                  color={d.signed ? 'success' : 'default'} 
                                  size="small" 
                                  sx={{ width: 'fit-content' }} 
                                />
                              </Box>
                              <Box display="flex" gap={1} flexWrap="wrap">
                                <Button 
                                  variant="contained" 
                                  color="primary" 
                                  size="medium"
                                  onClick={() => openDoc(d.id)}
                                  sx={{ 
                                    fontWeight: 'bold',
                                    minWidth: 120,
                                    boxShadow: 2,
                                    '&:hover': {
                                      boxShadow: 4,
                                      transform: 'translateY(-1px)'
                                    }
                                  }}
                                >
                                  Atidaryti
                                </Button>
                                {hasPermission('training.edit') && (
                                  <>
                                    <Button size="small" variant="outlined" onClick={() => loadSignoffStatus(d.id)}>Peržiūrėti pasirašymus</Button>
                                    <Button size="small" variant="outlined" onClick={() => openEditor(d.id)}>Redaguoti</Button>
                                    <Button size="small" variant="outlined" color={d.active ? 'warning' : 'success'} onClick={() => archiveDocument(d.id, !d.active)}>
                                      {d.active ? 'Archyvuoti' : 'Atkurti'}
                                    </Button>
                                    <Button size="small" variant="outlined" color="error" onClick={() => deleteDocument(d.id)}>Ištrinti</Button>
                                  </>
                                )}
                              </Box>
                            </Box>
                          } 
                        />
                      </ListItem>
                      <Divider />
                    </React.Fragment>
                  ))
                )}
              </List>
            </CardContent>
          </Card>
        </Box>
        <Box flex={2}>
          {selected && (
            <Card>
              <CardContent>
                <Typography variant="h6">{selected.document.title}</Typography>
                {selected.document.summary && (
                  <Typography variant="body2" sx={{ mb: 1 }}>{selected.document.summary}</Typography>
                )}
                {selected.document.body_html ? (
                  <Box sx={{ my: 2 }} dangerouslySetInnerHTML={{ __html: selected.document.body_html }} />
                ) : (
                  selected.document.content_url && (
                    <Button href={selected.document.content_url} target="_blank">Atidaryti turinį</Button>
                  )
                )}
                {selected.quiz && (
                  <Box mt={2}>
                    <Typography variant="subtitle1">Testas (min. {selected.quiz.passing_score}%)</Typography>
                    {selected.signed ? (
                      <Box mt={2}>
                        <Chip label="Pasirašyta" color="success" sx={{ mb: 1 }} />
                        {selected.score !== null && selected.score !== undefined && (
                          <Typography variant="body2" sx={{ mt: 1 }}>Balas: {selected.score}%</Typography>
                        )}
                      </Box>
                    ) : (
                      <>
                        {selected.quiz.questions.map((q: any) => (
                          <Box key={q.id} my={1}>
                            <Typography>{q.text}</Typography>
                            <RadioGroup
                              value={answers[q.id] || ''}
                              onChange={(e) => setAnswers(prev => ({ ...prev, [q.id]: e.target.value }))}
                            >
                              {q.choices.map((c: any) => (
                                <FormControlLabel key={c.id} value={c.id} control={<Radio />} label={c.text} />
                              ))}
                            </RadioGroup>
                          </Box>
                        ))}
                        <Button disabled={submitting} variant="contained" onClick={submit}>Pasirašyti</Button>
                      </>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          )}
        </Box>
      </Box>
      
      {/* Sign-off Status Dialog */}
      <Dialog open={signoffDialogOpen} onClose={() => setSignoffDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {signoffData?.document ? `Pasirašymų būsena: ${signoffData.document.title}` : 'Pasirašymų būsena'}
        </DialogTitle>
        <DialogContent>
          {loadingSignoffs ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : signoffData ? (
            <Box>
              <Box mb={2}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Versija:</strong> {signoffData.document.version}
                </Typography>
                <Typography variant="body1">
                  <strong>Bendrai priskirta:</strong> {signoffData.summary.totalAssigned} | 
                  <strong style={{ color: '#2e7d32', marginLeft: '8px' }}> Pasirašė:</strong> {signoffData.summary.totalSigned} | 
                  <strong style={{ color: '#d32f2f', marginLeft: '8px' }}> Nepasirašė:</strong> {signoffData.summary.totalNotSigned}
                </Typography>
              </Box>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Vardas, pavardė</strong></TableCell>
                      <TableCell><strong>El. paštas</strong></TableCell>
                      <TableCell><strong>Skyrius</strong></TableCell>
                      <TableCell align="center"><strong>Statusas</strong></TableCell>
                      <TableCell align="center"><strong>Balas</strong></TableCell>
                      <TableCell><strong>Pasirašymo data</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {signoffData.users.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">Nėra priskirtų vartotojų</TableCell>
                      </TableRow>
                    ) : (
                      signoffData.users.map((u: any) => (
                        <TableRow key={u.user_id}>
                          <TableCell>{`${u.firstName || ''} ${u.lastName || ''}`.trim() || '-'}</TableCell>
                          <TableCell>{u.email || '-'}</TableCell>
                          <TableCell>{u.department || '-'}</TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={u.signed ? 'Pasirašyta' : 'Nepasirašyta'} 
                              color={u.signed ? 'success' : 'default'} 
                              size="small" 
                            />
                          </TableCell>
                          <TableCell align="center">
                            {u.signed && u.score !== null ? `${u.score}%` : '-'}
                          </TableCell>
                          <TableCell>
                            {u.signed && u.signed_at ? new Date(u.signed_at).toLocaleString('lt-LT') : '-'}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSignoffDialogOpen(false)}>Uždaryti</Button>
          </DialogActions>
        </Dialog>
      
      {/* Editor Dialog */}
      <Dialog open={editorOpen} onClose={closeEditor} maxWidth="lg" fullWidth>
        <DialogTitle>
          {editingDocId ? 'Redaguoti dokumentą' : 'Naujas dokumentas'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="h6">Dokumento informacija</Typography>
            <TextField
              label="Pavadinimas *"
              value={editorTitle}
              onChange={(e) => setEditorTitle(e.target.value)}
              fullWidth
            />
            <TextField
              label="Slug (neprivaloma)"
              value={editorSlug}
              onChange={(e) => setEditorSlug(e.target.value)}
              fullWidth
            />
            <TextField
              label="Santrauka"
              value={editorSummary}
              onChange={(e) => setEditorSummary(e.target.value)}
              fullWidth
              multiline
              rows={2}
            />
            <TextField
              label="Žymės (kableliais)"
              value={editorTags}
              onChange={(e) => setEditorTags(e.target.value)}
              fullWidth
            />
            <TextField
              label="Aprašymas"
              value={editorDescription}
              onChange={(e) => setEditorDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
            />
            <TextField
              label="Turinys (HTML)"
              value={editorBodyHtml}
              onChange={(e) => setEditorBodyHtml(e.target.value)}
              fullWidth
              multiline
              rows={10}
            />
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="h6">Testas</Typography>
            <TextField
              label="Reikalingas balas %"
              type="number"
              value={editorPassingScore}
              onChange={(e) => setEditorPassingScore(parseInt(e.target.value || '80', 10))}
              sx={{ width: 200 }}
            />
            
            {editorQuestions.map((q, qi) => (
              <Card key={q.id} variant="outlined" sx={{ p: 2 }}>
                <Box display="flex" alignItems="center" gap={2} mb={1}>
                  <TextField
                    label={`Klausimas ${qi + 1}`}
                    value={q.text}
                    onChange={(e) => updateQuestionText(q.id, e.target.value)}
                    fullWidth
                  />
                  <Button color="error" onClick={() => removeQuestion(q.id)}>Pašalinti</Button>
                </Box>
                <Box mt={1}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Pažymėkite teisingą atsakymą:
                  </Typography>
                  {q.choices.map((c, ci) => (
                    <Box key={c.id} display="flex" alignItems="center" gap={1} my={0.5}>
                      <FormControlLabel
                        control={
                          <Radio
                            checked={c.is_correct}
                            onChange={() => markCorrect(q.id, c.id)}
                          />
                        }
                        label="Teisingas"
                        labelPlacement="end"
                        sx={{ minWidth: 100 }}
                      />
                      <TextField
                        label={`Atsakymas ${ci + 1}`}
                        value={c.text}
                        onChange={(e) => updateAnswerText(q.id, c.id, e.target.value)}
                        fullWidth
                      />
                      <Button color="error" onClick={() => removeAnswer(q.id, c.id)}>Šalinti</Button>
                    </Box>
                  ))}
                  <Button onClick={() => addAnswer(q.id)} sx={{ mt: 1 }}>Pridėti atsakymą</Button>
                </Box>
              </Card>
            ))}
            <Button onClick={addQuestion} variant="outlined">Pridėti klausimą</Button>
            
            {editingDocId && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6">Priskirti dokumentą</Typography>
                <RadioGroup row value={assignMode} onChange={(e) => setAssignMode(e.target.value as any)}>
                  <FormControlLabel value="all" control={<Radio />} label="Visiems" />
                  <FormControlLabel value="department" control={<Radio />} label="Pagal skyrių" />
                  <FormControlLabel value="users" control={<Radio />} label="Konkretiems vartotojams" disabled />
                </RadioGroup>
                {assignMode === 'department' && (
                  <TextField
                    label="Skyrius"
                    value={assignDept}
                    onChange={(e) => setAssignDept(e.target.value)}
                    sx={{ minWidth: 300 }}
                  />
                )}
                {assignMode === 'users' && (
                  <Autocomplete
                    multiple
                    options={allUsersOptions}
                    value={assignUsers}
                    onChange={(e, val) => setAssignUsers(val)}
                    getOptionLabel={(o) => o.label}
                    renderInput={(params) => (
                      <TextField {...params} label="Vartotojai" placeholder="Pasirinkite vartotojus" />
                    )}
                    sx={{ maxWidth: 600, my: 1 }}
                  />
                )}
                <TextField
                  label="Terminas (YYYY-MM-DD)"
                  value={dueAt}
                  onChange={(e) => setDueAt(e.target.value)}
                  sx={{ mt: 1 }}
                  placeholder="2025-12-31"
                />
                <Button
                  variant="contained"
                  onClick={assignDocument}
                  disabled={assigning}
                  sx={{ mt: 2 }}
                >
                  {assigning ? 'Priskiriama...' : 'Priskirti'}
                </Button>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditor} disabled={editorSubmitting || assigning}>Atšaukti</Button>
          <Button 
            variant="contained" 
            onClick={saveDocument} 
            disabled={editorSubmitting || !editorTitle.trim()}
          >
            {editorSubmitting ? 'Išsaugoma...' : (editingDocId ? 'Atnaujinti' : 'Sukurti')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    );
  };

export default Mokymai;


